import { useMemo } from "react";
import {
  MarkdownEditor,
  renderMarkdownToHtml,
} from "@norkron/web-markdown-editor";
import type { ArticleResource } from "../../api/articleUnits";
import { ArrowRight } from "../../compose/Icon";

/**
 * `<ResearchColumn>` — the research pane BODY (design §2.6): one column, a
 * vertical stack of markdown section cards (never multiple grid columns). Read
 * mode renders each heading-delimited snippet with the lib's
 * `renderMarkdownToHtml` (deliberately plain — agents consume the raw md); each
 * card shows a "↳ Article §N" chip that scrolls the linked article block. Edit
 * mode mounts ONE `@norkron/web-markdown-editor` `<MarkdownEditor>` over the
 * whole research doc (avoids N mounted editors + save races). This is the
 * research half of the two-editor boundary — research NEVER mounts a DoczCanvas.
 */
export interface ResearchColumnProps {
  readonly resource: ArticleResource;
  readonly editable: boolean;
  /** Controlled markdown value while editing. */
  readonly value: string;
  readonly onChange: (md: string) => void;
  /** Fired by a card's "↳ Article §N" chip (scrolls the article pane). */
  readonly onArticleChip?: (sectionIndex: number) => void;
}

interface MdSection {
  heading: string;
  body: string;
}

/** Split raw markdown into heading-delimited snippets (one card each). Content
 *  before the first heading becomes an untitled lead card. */
export function splitMarkdownSections(md: string): MdSection[] {
  const lines = md.split("\n");
  const out: MdSection[] = [];
  let current: MdSection | null = null;
  for (const line of lines) {
    const m = /^#{1,6}\s+(.*)$/.exec(line);
    if (m) {
      if (current) out.push(current);
      current = { heading: m[1].trim(), body: `${line}\n` };
    } else {
      if (!current) current = { heading: "", body: "" };
      current.body += `${line}\n`;
    }
  }
  if (current) out.push(current);
  return out.filter((s) => s.body.trim().length > 0);
}

export function ResearchColumn({
  resource,
  editable,
  value,
  onChange,
  onArticleChip,
}: ResearchColumnProps) {
  const sections = useMemo(() => splitMarkdownSections(value), [value]);

  if (editable) {
    return (
      <div className="wb-research wb-research--edit" data-testid="research-editor">
        <MarkdownEditor
          value={value}
          onChange={onChange}
          testId="research-markdown-editor"
          ariaLabel="Research markdown"
        />
      </div>
    );
  }

  return (
    <div className="wb-research" data-testid="research-column">
      {sections.map((section, i) => (
        <article
          // biome-ignore lint/suspicious/noArrayIndexKey: sections are positional snippets (stable within a render of the same md).
          key={i}
          className="wb-research__card"
          data-testid={`research-card-${i}`}
        >
          {/* Raw-markdown → HTML via the lib's default renderer (plain by design).
            *
            * WHAT THE LIB ACTUALLY GUARANTEES — stated narrowly on purpose. The
            * comment that stood here claimed `renderMarkdownToHtml` "escapes all
            * input before formatting", and that was never true: link and image
            * URLs were interpolated with no scheme check, so
            * `[go](javascript:alert%281%29)` rendered a LIVE `javascript:` href
            * in this very column. Fixed upstream in web-markdown-editor
            * `d328d57`. The guarantee is now two separate things:
            *   · text is HTML-escaped (`<script>` arrives as `&lt;script&gt;`);
            *   · every URL is gated by the ecosystem scheme policy
            *     (`isSafeDocumentHref` / `isSafeImageSrc`, mirroring docz's
            *     `html_escape`). A rejected href renders INERT as
            *     `<a class="docz-blocked-href">label</a>` — no href, label kept —
            *     and a rejected image renders as `<img alt="…">` with `src`
            *     dropped.
            * It is NOT a general-purpose HTML sanitiser, and this comment must
            * not grow into claiming it is: overstating the property is why
            * nobody re-read the renderer for months.
            */}
          {/* biome-ignore lint/security/noDangerouslySetInnerHtml: renderMarkdownToHtml HTML-escapes text and gates every href/src through the lib's URL-scheme policy (see the note above). */}
          <div
            className="wb-research__body ela-doc"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: see above.
            dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(section.body) }}
          />
          <button
            type="button"
            className="wb-research__chip"
            data-testid={`research-article-chip-${i}`}
            onClick={() => onArticleChip?.(i)}
            title="Jump to the linked article section"
          >
            <ArrowRight size={12} strokeWidth={1.5} />
            Article §{i + 1}
          </button>
        </article>
      ))}
      {sections.length === 0 ? (
        <p className="wb-research__empty" data-testid="research-empty">
          No research captured yet — {resource.title}.
        </p>
      ) : null}
    </div>
  );
}
